/**
 * PartnerScriptRunner
 *
 * Executes DB-backed per-partner adapter scripts inside the host page.
 *
 * Backend flow:
 *   1. On session start (after flavor is applied), backend sends
 *      `{ t: 'partner_bundle', partner_id, platform, adapters: { action: {...} } }`.
 *      Adapters are object-keyed because action is a unique key within a
 *      partner's bundle — the DB constraint enforces it, the voice LLM's
 *      tool schema assumes it.
 *   2. When the LLM calls e.g. add_to_cart, backend emits a client_tool_call:
 *        toolName: 'run_partner_script'
 *        parameters: { action: 'cart.add', args: { sku, qty } }
 *
 * Compile-on-load: each adapter is compiled into an AsyncFunction immediately
 * when the bundle lands. Syntax errors are isolated (poison markers) — one
 * bad adapter does not brick the bundle, and the error shows up in the
 * console when it can still be fixed, not at first call time in whichever
 * session happens to hit it.
 *
 * Adapter contract (stored in ttp.partner_adapters.code_js):
 *   async (ctx) => { ...; return { ok: true, ... }; }
 *
 * ctx provided to every adapter:
 *   - args         : parameters from the LLM
 *   - host         : location.hostname
 *   - platform     : bundle-level platform ('hybris' | 'shopify' | ...)
 *   - fetch        : bound window.fetch
 *   - log          : console.log with an [adapter:<action>] prefix
 *   - refreshUI    : platform-aware UI nudge
 *   - normalizeCart: raw-cart → canonical cart (platform-dispatched)
 *   - runAdapter   : (action, args) → Promise<result>  (invokes another adapter)
 */

const LOG = '[PartnerScriptRunner]';

// ---------------------------------------------------------------------------
// Compilation
// ---------------------------------------------------------------------------

const AsyncFunction = Object.getPrototypeOf(async function () {}).constructor;

// Wrap and compile an adapter body. The body is stored as an arrow function
// expression, so we wrap it, evaluate, and invoke. AsyncFunction (not
// `new Function`) enforces an async execution context — no accidental sync
// adapter bodies.
function compileAdapter(codeJs) {
  // eslint-disable-next-line no-new-func
  return new AsyncFunction('ctx', `"use strict"; return (${codeJs}).call(null, ctx);`);
}

// Cheap 32-bit string hash, rendered as 12 hex chars. NOT cryptographic —
// good enough for "did the bundle change between pushes?" diagnostics.
function shortHash(s) {
  let h = 0x811c9dc5; // FNV-like seed
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0).toString(16).padStart(8, '0').repeat(2).slice(0, 12);
}

// ---------------------------------------------------------------------------
// Cart normalization — canonical shape derived in the widget, never passed
// through from the raw platform response. Keeps the LLM looking at clean truth.
// ---------------------------------------------------------------------------

function emptyCart() {
  return { itemCount: 0, lineCount: 0, total: 0, currency: null, totalFormatted: null, entries: [] };
}

function normalizeHybrisCart(raw) {
  if (!raw || typeof raw !== 'object') return emptyCart();
  const rawEntries = Array.isArray(raw.entries) ? raw.entries : [];

  const entries = rawEntries.map((e) => {
    const product = e.product || {};
    const quantity = Number.isFinite(e.quantity) ? e.quantity : (+e.quantity || 0);

    const unitBefore   = e.basePrice?.value ?? product.price?.value ?? null;
    const unitDiscount = e.discountPrice?.value ?? null;
    const unitEffective = unitDiscount != null ? unitDiscount : unitBefore;
    const onSale       = unitDiscount != null && unitBefore != null && unitDiscount < unitBefore;

    const lineTotal = e.totalPrice?.value
                   ?? (unitEffective != null ? +(unitEffective * quantity).toFixed(2) : null);
    const lineTotalBefore = onSale && unitBefore != null
                   ? +(unitBefore * quantity).toFixed(2)
                   : null;

    return {
      sku:              e.productCode ?? product.code ?? null,
      ean:              e.ean ?? product.ean ?? null,
      name:             e.productName ?? product.name ?? product.title ?? null,
      brand:            e.brand ?? product.brand ?? null,
      quantity,
      unitPrice:        unitEffective,
      unitPriceBefore:  onSale ? unitBefore : null,
      lineTotal,
      lineTotalBefore,
      onSale,
      imageUrl:         e.imageUrl ?? product.desktopImage?.url ?? product.image?.url ?? null,
      productUrl:       e.productUrl ?? product.url ?? null,
      maxQuantity:      e.maxOrderQuantity ?? null,
      entryId:          e.entryId ?? null,
    };
  });

  const lineCount = entries.length;
  const itemCount = entries.reduce((n, x) => n + (x.quantity || 0), 0);
  const total = raw.totalPrice?.value
             ?? raw.subTotal?.value
             ?? entries.reduce((s, x) => s + (x.lineTotal || 0), 0);
  const currency = raw.totalPrice?.currencyIso ?? raw.currencyIso ?? null;
  const totalFormatted = raw.totalPrice?.formattedValue
                      ?? (total != null && currency ? `${currency} ${total.toFixed(2)}` : null);

  return { itemCount, lineCount, total, currency, totalFormatted, entries };
}

function normalizeCartByPlatform(platform, raw) {
  switch ((platform || '').toLowerCase()) {
    case 'hybris': return normalizeHybrisCart(raw);
    // shopify / woocommerce / custom: stubs until we onboard them. Hybris
    // shape is the closest thing to canonical, so default to it.
    default:       return normalizeHybrisCart(raw);
  }
}

// Best-effort UI nudge after a cart mutation. No-ops when the platform's
// globals aren't present, so adapters can always call ctx.refreshUI().
function refreshUIForPlatform(platform) {
  return () => {
    try {
      if ((platform || '').toLowerCase() === 'hybris') {
        if (window.minicart?.updateCart) window.minicart.updateCart(false);
      }
    } catch (e) {
      console.warn(LOG, 'refreshUI failed:', e);
    }
  };
}

// ---------------------------------------------------------------------------
// Runner
// ---------------------------------------------------------------------------

export class PartnerScriptRunner {
  constructor() {
    /** @type {Map<string, {fn?: Function, version?: number, codeSha256?: string, meta?: object, poisoned?: boolean, error?: string}>} */
    this.adapters = new Map();
    this.partnerId = null;
    this.platform  = null;
    this.bundleVersion = 0; // monotonic, bumped on every setBundle
  }

  /**
   * Replace the cached bundle from a `partner_bundle` message.
   * @param {{partner_id?: string, platform?: string|null, adapters?: Record<string,{version?:number,code_js:string,code_sha256?:string,meta?:object}>}} msg
   */
  setBundle(msg) {
    const adapters = msg?.adapters || {};
    this.partnerId = msg?.partner_id || null;
    this.platform  = msg?.platform || null;
    this.adapters  = new Map();

    let compiled = 0;
    let poisoned = 0;

    for (const [action, entry] of Object.entries(adapters)) {
      if (!entry || typeof entry.code_js !== 'string') {
        console.warn(LOG, `skipping malformed adapter '${action}':`, entry);
        this.adapters.set(action, {
          poisoned: true,
          error: 'missing code_js',
          version: entry?.version ?? null,
        });
        poisoned++;
        continue;
      }
      try {
        const fn = compileAdapter(entry.code_js);
        this.adapters.set(action, {
          fn,
          version:    entry.version ?? null,
          codeSha256: entry.code_sha256 || null,
          meta:       entry.meta || {},
        });
        compiled++;
      } catch (err) {
        console.error(LOG, `compile failed for '${action}':`, err);
        this.adapters.set(action, {
          poisoned: true,
          error:    String(err?.message || err),
          version:  entry.version ?? null,
        });
        poisoned++;
      }
    }

    this.bundleVersion += 1;
    const sha = shortHash(JSON.stringify(adapters));
    console.log(
      `${LOG} bundle loaded partner=${this.partnerId} platform=${this.platform} ` +
      `actions=${this.adapters.size} (ok=${compiled} poisoned=${poisoned}) ` +
      `sha=${sha} v${this.bundleVersion}`
    );
    if (poisoned > 0) {
      const bad = [...this.adapters.entries()]
        .filter(([, v]) => v.poisoned)
        .map(([a, v]) => `${a}: ${v.error}`);
      console.warn(`${LOG} poisoned actions:`, bad);
    }
  }

  has(action) { return this.adapters.has(action); }
  get(action) { return this.adapters.get(action); }

  /**
   * Execute an adapter by action name. Used both by the run_partner_script
   * tool handler and by ctx.runAdapter for intra-bundle chaining.
   */
  async runAdapter(action, args = {}) {
    const entry = this.adapters.get(action);
    if (!entry) {
      return { ok: false, reason: 'adapter_not_in_bundle', action };
    }
    if (entry.poisoned) {
      return { ok: false, reason: 'adapter_compile_error', action, detail: entry.error };
    }

    const ctx = this._buildCtx(args, action, entry);
    console.log(
      `${LOG} exec ${action} v${entry.version ?? '?'} ` +
      `sha=${(entry.codeSha256 || '').slice(0, 12)}`
    );

    try {
      return await entry.fn(ctx);
    } catch (e) {
      console.error(`${LOG} ${action} threw:`, e);
      return { ok: false, reason: 'adapter_threw', error: String(e?.message || e) };
    }
  }

  _buildCtx(args, action /*, entry */) {
    const platform = this.platform;
    return {
      args:          args || {},
      host:          window.location.hostname,
      platform,
      fetch:         window.fetch.bind(window),
      log:           (...a) => console.log(`[adapter:${action}]`, ...a),
      refreshUI:     refreshUIForPlatform(platform),
      normalizeCart: (raw) => normalizeCartByPlatform(platform, raw),
      runAdapter:    (a, subArgs) => this.runAdapter(a, subArgs),
    };
  }
}

// ---------------------------------------------------------------------------
// Client-tool handler
// ---------------------------------------------------------------------------

/**
 * Build the { name, handler } entry consumed by BaseFlavor.buildTools().
 * Backend sends: parameters = { action, args, ... }
 */
export function buildRunPartnerScriptTool(runner) {
  return {
    name: 'run_partner_script',
    handler: async (parameters = {}) => {
      const { action, args } = parameters;
      if (!action) {
        return { ok: false, reason: 'missing_action' };
      }
      return runner.runAdapter(action, args || {});
    },
  };
}
